// Versao do AmarelinhoService para o modo teste (ver audioTestMode.ts).
//
// Herda o servico real: as consultas publicas (CEP, cargos) continuam iguais.
// O que muda:
//   - validacoes de CPF e e-mail: sempre sucesso, sem chamar a API;
//   - saves: nao enviam nada, so registram o payload em AudioTestCaptures;
//   - login: usuario ficticio, para as etapas seguintes nao redirecionarem;
//   - listas que exigem token (deficiencias, faixas salariais): fixtures.

import { SignupModel, SignupModelHelper } from '../data/SignupModel';
import { DeficiencyDto } from '../data/dto/deficiency.dto';
import { LoggedUser } from '../data/dto/logged-user.dto';
import { SalaryExpectationDto } from '../data/dto/salary-expectation.dto';
import AmarelinhoServiceReal from './AmarelinhoService.real';
import { AudioTestCaptures } from './audioTestMode';
import { ServiceResponse } from './ServiceModels';
import { UserService } from './UserService';

const FAKE_USER_ID = 999999;
const FAKE_COOKIES = { sessionCookie: 'audio-test-session', authCookie: 'audio-test-auth' };

const FAKE_DEFICIENCIES: DeficiencyDto[] = [
  { id: 1, name: 'Não possuo deficiência' },
  { id: 2, name: 'Física' },
  { id: 3, name: 'Auditiva' },
  { id: 4, name: 'Visual' },
  { id: 5, name: 'Intelectual' },
  { id: 6, name: 'Múltipla' }
];

const FAKE_SALARY_EXPECTATIONS: SalaryExpectationDto[] = [
  { id: 1, description: 'Até R$ 1.500', minValue: 0 },
  { id: 2, description: 'De R$ 1.500 a R$ 2.500', minValue: 1500 },
  { id: 3, description: 'De R$ 2.500 a R$ 4.000', minValue: 2500 },
  { id: 4, description: 'De R$ 4.000 a R$ 6.000', minValue: 4000 },
  { id: 5, description: 'Acima de R$ 6.000', minValue: 6000 }
];

const OK: ServiceResponse = { success: true };

function capture(method: string, httpMethod: 'POST' | 'PATCH', url: string, payload: unknown): ServiceResponse {
  AudioTestCaptures.record({ service: 'AmarelinhoService', method, httpMethod, url, payload });
  return { ...OK };
}

function cvUrl(path: string) {
  return `/user/${FAKE_USER_ID}/curriculum${path}`;
}

export default class AmarelinhoServiceFake extends AmarelinhoServiceReal {
  static async validateEmail(email: string): Promise<ServiceResponse> {
    return capture('validateEmail', 'POST', '/user/validate-email', { email });
  }

  static async validateCPF(cpf: string, nome: string): Promise<ServiceResponse> {
    return capture('validateCPF', 'POST', '/user/validate-cpf', { cpf });
  }

  static async listDeficiencies(): Promise<DeficiencyDto[] | null> {
    return FAKE_DEFICIENCIES;
  }

  static async listSalaryExpectations(): Promise<SalaryExpectationDto[] | null> {
    return FAKE_SALARY_EXPECTATIONS;
  }

  static async saveAccount(signup: SignupModel): Promise<any> {
    const dto = SignupModelHelper.toSignUpDto(signup);
    capture('saveAccount', 'POST', '/user', dto);

    const user: LoggedUser = {
      id: FAKE_USER_ID,
      name: signup.name,
      email: signup.email,
      token: 'audio-test-token',
      cookie: FAKE_COOKIES
    };
    UserService.setLoggedUser(user);

    return { ...OK, id: user.id, email: user.email, name: user.name };
  }

  static async isLogado(): Promise<any> {
    const user = UserService.getLoggedUser();
    if (!user) return { success: false, message: 'Usuário não está logado.' };
    return { success: true, userId: user.id };
  }

  static async saveExtraPersonalData(signup: SignupModel): Promise<ServiceResponse> {
    return capture('saveExtraPersonalData', 'POST', cvUrl('/personal-data'), {
      cookies: FAKE_COOKIES,
      personalData: {
        nationality: signup.nationality,
        maritalStatus: signup.maritalStatus?.value,
        deficiencyId: signup.deficiency?.id
      }
    });
  }

  static async saveSalaryExpectation(signup: SignupModel): Promise<ServiceResponse> {
    return capture('saveSalaryExpectation', 'POST', cvUrl('/salary-expectation'), {
      salaryExpectationId: signup.salaryExpectation?.id
    });
  }

  static async saveExperiences(signup: SignupModel): Promise<any> {
    return capture('saveExperiences', 'POST', cvUrl('/experiences'), {
      experiences: SignupModelHelper.toAmarelinhoExperiencesDto(signup),
      cookies: FAKE_COOKIES
    });
  }

  static async saveCvExtraData(signup: SignupModel): Promise<ServiceResponse> {
    return capture('saveCvExtraData', 'POST', cvUrl('/experiences'), {
      experiences: SignupModelHelper.toAmarelinhoExperiencesDto(signup),
      cookies: FAKE_COOKIES
    });
  }

  static async saveObjectives(signup: SignupModel): Promise<ServiceResponse> {
    return capture('saveObjectives', 'POST', cvUrl('/desired-positions'), {
      desiredPositions: SignupModelHelper.toAmarelinhoDesiredPositionsDto(signup),
      cookies: FAKE_COOKIES
    });
  }

  static async saveLanguages(signup: SignupModel): Promise<any> {
    return capture('saveLanguages', 'POST', cvUrl('/languages'), {
      languages: SignupModelHelper.toAmarelinhoLanguageDto(signup),
      cookies: FAKE_COOKIES
    });
  }

  static async savePresentationLetter(signup: SignupModel): Promise<any> {
    return capture('savePresentationLetter', 'POST', cvUrl('/presentation-letter'), {
      presentationLetter: SignupModelHelper.toAmarelinhoPresentationLetterDto(signup),
      cookies: FAKE_COOKIES
    });
  }

  static async saveGraduation(signup: SignupModel): Promise<ServiceResponse> {
    return capture('saveGraduation', 'POST', cvUrl('/schooling'), {
      schoolingList: SignupModelHelper.toAmarelinhoSchoolingListDto(signup),
      cookies: FAKE_COOKIES
    });
  }

  static async saveCourses(signup: SignupModel): Promise<ServiceResponse> {
    return capture('saveCourses', 'POST', cvUrl('/courses'), {
      courses: SignupModelHelper.toAmarelinhoCoursesDto(signup),
      cookies: FAKE_COOKIES
    });
  }
}
